import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import { useNavigate } from "react-router";
import { useState, useRef, useEffect } from "react";
import * as FaIcons from "react-icons/fa";
import * as XLSX from "xlsx";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import swal from "sweetalert";
import CustomPhoneInput from "../Agents/InputField";
import { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";

function AddLeads() {
  // Normalize any phone-like input to E.164 (e.g. +14155552671)
  const normalizeToE164 = (raw, defaultCountry = "PK") => {
    if (!raw) return null;
    try {
      const parsed = parsePhoneNumber(String(raw), defaultCountry);
      if (parsed && parsed.isValid()) return parsed.number; // E.164
      return null;
    } catch (e) {
      return null;
    }
  };

  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fileData, setFileData] = useState(null);
  const [validationError, setValidationError] = useState("");
  const [backendErrors, setBackendErrors] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [singleLead, setSingleLead] = useState({
    client_name: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});
  const requiredHeaders = [
    "Client Name",
    "Email",
    "Phone",
    "City",
    "Destination Country",
    "Destination State",
    "Previous Education",
    "Intended Course",
    "Year of Passing",
    "CGPA",
    "Percentage",
    "English Test",
    "Time Slot",
    "Mode of Class",
    "Document Links",
    "Budget Min",
    "Budget Max",
    "Study Gap (Years)",
    "Refusal History",
    "Visa Type",
  ];
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [allCampaign, setAllCampaign] = useState([]);
  const [leadCreatedAt, setLeadCreatedAt] = useState(null);

  const fetchCampaigns = async () => {
    try {
      const response = await apiUrl.get("/getAllActiveCampaign");
      if (response?.status) {
        setAllCampaign(response?.data?.data);
      }
    } catch (err) {
      console.error("Failed to load campaigns", err);
    }
  };

  // when component load it first fetch the permission
  useEffect(() => {
    const perms = getPermissions();
    if (perms) {
      setPermissions(perms);
      fetchCampaigns();
    }
  }, []);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (isExcelFile(file)) {
        setUploadedFile(file);
        readExcelFile(file);
      } else {
        swal("Please upload only Excel files (.xlsx, .xls)", "", "info");
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (isExcelFile(file)) {
        setUploadedFile(file);
        readExcelFile(file);
      } else {
        swal("Please upload only Excel files (.xlsx, .xls)", "", "info");
      }
    }
  };

  const isExcelFile = (file) => {
    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    return (
      allowedTypes.includes(file.type) ||
      file.name.endsWith(".xlsx") ||
      file.name.endsWith(".xls")
    );
  };

  const readExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        if (jsonData.length <= 1) {
          setValidationError(
            "File has no data rows. Please provide valid leads."
          );
          return;
        }
        const headers = jsonData[0].map((header) =>
          header !== undefined && header !== null
            ? header.toString().trim()
            : ""
        );
        const headerValidation = validateHeaders(headers);
        if (!headerValidation.isValid) {
          setValidationError(headerValidation.error);
          return;
        }
        const dataRows = jsonData.slice(1);
        const processedData = processExcelData(dataRows, headers);

        setFileData(processedData);
        setValidationError("");
        setBackendErrors(null);
      } catch (error) {
        console.error("Error reading file:", error);
        setValidationError(
          "Error reading file. Please ensure it's a valid Excel file."
        );
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const validateHeaders = (headers) => {
    const missingHeaders = requiredHeaders.filter(
      (required) =>
        !headers.some(
          (header) =>
            header &&
            header.toString().trim().toLowerCase() === required.toLowerCase()
        )
    );
    if (missingHeaders.length > 0) {
      return {
        isValid: false,
        error: `Missing required headers: ${missingHeaders.join(", ")}`,
      };
    }
    return { isValid: true };
  };

  const processExcelData = (rows, headers) => {
    return rows
      .map((row, index) => {
        const processedRow = {};
        headers.forEach((header, colIndex) => {
          const value = row[colIndex];
          processedRow[header] =
            value !== undefined && value !== null && value !== ""
              ? value
              : null;
        });
        // Normalize phone to E.164; skip row if invalid
        const normalizedPhone = normalizeToE164(processedRow.Phone, "PK");

        if (normalizedPhone) {
          return {
            client_name: processedRow["Client Name"] || null,
            email: processedRow.Email || null,
            phone: normalizedPhone,
            city: processedRow.City || null,
            destination_country: processedRow["Destination Country"] || null,
            state: processedRow["Destination State"] || null,
            education: processedRow["Previous Education"] || null,
            course: processedRow["Intended Course"] || null,
            passing_year: processedRow["Year of Passing"] || null,
            academic_cgpa: processedRow["CGPA"] || null,
            academic_percentage: processedRow["Percentage"] || null,
            eng_test: processedRow["English Test"] || null,
            time_slot: processedRow["Time Slot"] || null,
            mode_of_class: processedRow["Mode of Class"] || null,
            documents_link: processedRow["Document Links"] || null,
            budget_min: processedRow["Budget Min"]
              ? parseFloat(processedRow["Budget Min)"])
              : null,
            budget_max: processedRow["Budget Max)"]
              ? parseFloat(processedRow["Budget Max)"])
              : null,
            study_gap_months:
              processedRow["Study Gap (Years)"] &&
              !isNaN(parseFloat(processedRow["Study Gap (Years)"]))
                ? Math.round(parseFloat(processedRow["Study Gap (Years)"]) * 12)
                : null,
            refusal_count:
              processedRow["Refusal History"] &&
              !isNaN(parseInt(processedRow["Refusal History"]))
                ? parseInt(processedRow["Refusal History"])
                : null,
            visa: processedRow["Visa Type"] || null,
          };
        }
        return null;
      })
      .filter((row) => row !== null);
  };

  const handleUpload = async () => {
    if (!uploadedFile || !fileData || !selectedCampaign) {
      swal("Error", "Please select Campaign", "error");
      return;
    }
    if (!leadCreatedAt) {
      swal("Error", "Please select Created At", "error");
      return;
    }
    setUploading(true);
    try {
      const response = await apiUrl.post("/addLeads", {
        leads: fileData.map((lead) => ({
          ...lead,
        })),
        campaign_id: parseInt(selectedCampaign),
        created_at: leadCreatedAt,
      });
      const { status, message, saved, rejected, rejected_rows } = response.data;
      if (status) {
        swal(
          "Success",
          `${saved} leads imported successfully!`,
          "success"
        ).then(() => {
          removeFile();
          navigate("/leads");
        });
      } else {
        setBackendErrors({
          message,
          rejected,
          rejected_rows: rejected_rows || [],
        });
      }
    } catch (error) {
      const { message, saved, rejected, rejected_rows } = error.response.data;
      setBackendErrors({
        message: message || "File contains invalid or duplicate rows",
        rejected: rejected || 0,
        rejected_rows: rejected_rows || [],
      });
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setFileData(null);
    setValidationError("");
    setBackendErrors(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };
  // download sample file
  const downloadSampleFile = (fileType) => {
    const headers = [
      "Client Name",
      "Email",
      "Phone",
      "City",
      "Destination Country",
      "Destination State",
      "Previous Education",
      "Intended Course",
      "Year of Passing",
      "CGPA",
      "Percentage",
      "English Test",
      "Time Slot",
      "Mode of Class",
      "Document Links",
      "Budget Min",
      "Budget Max",
      "Study Gap (Years)",
      "Refusal History",
      "Visa Type",
    ];

    const educationLevels = [
      { value: "High School", label: "High School (Matric/O-Levels)" },
      { value: "Intermediate", label: "Intermediate" },
      { value: "Diploma", label: "Diploma / Certificate" },
      { value: "Bachelor", label: "Bachelor's Degree" },
      { value: "Master", label: "Master's Degree" },
      { value: "PhD", label: "PhD" },
      { value: "Other", label: "Other" },
    ];

    const cities = ["Islamabad", "Lahore", "Karachi", "Peshawar", "Quetta"];
    const countries = ["Australia", "UK", "Canada", "USA", "Germany"];
    const states = ["Queensland", "Ontario", "California", "London", "Berlin"];
    const courses = [
      "Computer Science",
      "Business Administration",
      "Cyber Security",
      "Mechanical Engineering",
      "Data Science",
      "Artificial Intelligence",
      "Medicine",
    ];
    const englishTests = ["Duolingo", "IELTS", "PTE", "TOEFL", "None"];
    const visaType = ["Student", "Sponsor", "PR", "Work"];
    const modeOfClass = ["Online", "Physical", "Hybrid"];

    // Generate 30 dummy rows
    const sampleData = Array.from({ length: 30 }, (_, i) => {
      const edu = educationLevels[i % educationLevels.length];
      const city = cities[i % cities.length];
      const country = countries[i % countries.length];
      const state = states[i % states.length];
      const course = courses[i % courses.length];
      const test = englishTests[i % englishTests.length];
      const visa = visaType[i % visaType.length];
      const mode = modeOfClass[i % modeOfClass.length];
      const timeslot = "9PM-10PM";

      return [
        `Client ${i + 1}`,
        `client${i + 1}@gmail.com`,
        `92345000000${(i % 9) + 1}`,
        city,
        country,
        state,
        edu.value,
        course,
        2015 + (i % 10),
        (2.5 + (i % 20) * 0.2).toFixed(2),
        50 + (i % 51),
        test,
        timeslot,
        mode,
        " ",
        3000000,
        5000000,
        i % 6,
        i % 2,
        visa,
      ];
    });

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...sampleData]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample");

    const ext = fileType === "xlsx" ? "xlsx" : "xls";
    const fileName = `Sample_Leads.${ext}`;
    XLSX.writeFile(workbook, fileName);
  };

  // add single lead
  const handleSaveSingleLead = async () => {
    if (!selectedCampaign) {
      swal("Error", "Please Select Campaign", "error");
      return;
    }
    const validationErrors = {};

    const normalizedPhone = normalizeToE164(singleLead?.phone, "PK");
    if (!isValidPhoneNumber(normalizedPhone || "")) {
      validationErrors.phone = "Invalid Phone Number";
    }

    if (/^\d+$/.test(singleLead.client_name.trim())) {
      validationErrors.client_name = "Client name cannot be only numbers";
    }

    if (singleLead.email && !/^\S+@\S+\.\S+$/.test(singleLead.email)) {
      validationErrors.email = "Email format is invalid";
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const leadData = {
      client_name: singleLead.client_name || null,
      date_of_birth: null,
      email: singleLead.email || null,
      phone: normalizedPhone,
      city: null,
      destination_country: null,
      state: null,
      education: null,
      course: null,
      visa: null,
      passing_year: null,
      academic_percentage: null,
      academic_cgpa: null,
      eng_test: null,
      budget_min: null,
      budget_max: null,
      study_gap_months: null,
      refusal_count: null,
    };

    try {
      const response = await apiUrl.post("/addLeads", {
        leads: [leadData],
        campaign_id: parseInt(selectedCampaign),
      });
      if (response.data.status) {
        swal("Success", "Lead added successfully!", "success").then(() => {
          setShowModal(false);
          setSingleLead({
            client_name: "",
            email: "",
            phone: "",
          });
          setErrors({});
          // Optionally refresh the leads list or redirect
          navigate("/leads");
        });
      } else {
        swal("Error", response.data.message || "Failed to add lead", "error");
      }
    } catch (err) {
      swal("Error", "Something went wrong!", "error");
      console.error(err);
    }
  };
  return (
    <>
      <style>
        {`
          .modal-backdrop-blur {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.5);
            backdrop-filter: blur(5px);
            z-index: 1040;
          }
          .modal {
            z-index: 1050;
          }
          .modal-header {
            background:#4b6cb7;
            color: #ffffff;
          }
          .modal-content {
            border-radius: 15px;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
            border: none;
          }
          .form-control, .form-select {
            border-radius: 8px;
            border: 2px solid #e9ecef;
            transition: all 0.3s ease;
            padding: 0.75rem 1rem;
          }
          .form-control:focus, .form-select:focus {
            border-color: #4b6cb7;
            box-shadow: 0 0 0 0.2rem rgba(75, 108, 183, 0.25);
            outline: none;
          }
          .form-label {
            color: #495057;
            font-weight: 600;
            margin-bottom: 0.5rem;
            font-size: 0.9rem;
          }
          
         
          .btn-secondary {
            background: #6c757d;
            border: none;
            border-radius: 8px;
            padding: 0.75rem 1.5rem;
            font-weight: 600;
            transition: all 0.3s ease;
          }
          .btn-secondary:hover {
            background: #5a6268;
            transform: translateY(-1px);
          }
          .section-card {
            background: #f8f9fa;
            border-radius: 12px;
            padding: 1.5rem;
            margin-bottom: 1.5rem;
            border: 1px solid #e9ecef;
          }
          .section-title {
            color: #4b6cb7;
            font-weight: 700;
            font-size: 1.1rem;
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #4b6cb7;
            display: inline-block;
          }
          .read-only-text {
            background: #f8f9fa;
            padding: 0.75rem 1rem;
            border-radius: 8px;
            border: 2px solid #e9ecef;
            color: #495057;
            font-weight: 500;
          }
          .modal-body {
            max-height: 70vh;
            overflow-y: auto;
          }
          .modal-body::-webkit-scrollbar {
            width: 6px;
          }
          .modal-body::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
          }
          .modal-body::-webkit-scrollbar-thumb {
            background: #4b6cb7;
            border-radius: 10px;
          }
        `}
      </style>
      <div className="container-fluid px-2 py-3">
        {!!permissions.delete_campaign_leads && (
          <div className="row justify-content-center">
            <div className="col-12 col-lg-12 px-4">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center">
                  <a
                    className="btn btn-link p-0 me-3"
                    onClick={() => navigate("/leads")}
                    style={{ textDecoration: "none" }}
                  >
                    <FaIcons.FaArrowAltCircleLeft size={22} className="me-1" />
                    <span className="fw-bold">Back</span>
                  </a>
                  <h2 className="mb-0 fw-bold">Add Campaign leads</h2>
                </div>

                {!!permissions.delete_campaign_leads && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setSingleLead({
                        client_name: "",
                        email: "",
                        phone: "",
                      });
                      setShowModal(true);
                    }}
                  >
                    <FaIcons.FaRegPlusSquare className="me-1" />
                    Add Single Lead
                  </button>
                )}
              </div>

              {!!permissions.delete_campaign_leads && (
                <div className="row mb-4 gap-3">
                  {/* Campaign Selection */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      Select Campaign <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={selectedCampaign}
                      onChange={(e) => setSelectedCampaign(e.target.value)}
                      required
                    >
                      <option value="" disabled>
                        -- Select a Campaign --
                      </option>
                      {allCampaign.map((camp) => (
                        <option key={camp.id} value={camp.id}>
                          {camp.name} ({camp.campaign_code}) -{" "}
                          {camp.created_at.split("T")[0]}
                        </option>
                      ))}
                    </select>
                    <small className="text-danger">
                      All uploaded leads will be assigned to this campaign.
                    </small>
                  </div>

                  {/* Created At (Leads Date) */}
                  <div className="col-md-2">
                    <label className="form-label fw-bold">
                      Leads Created At <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={leadCreatedAt}
                      onChange={(e) => setLeadCreatedAt(e.target.value)}
                      required
                    />
                    <small className="text-danger">
                      This date will be saved as <b>created_at</b> for uploaded
                      leads.
                    </small>
                  </div>
                </div>
              )}

              <div className="card shadow-lg border-0">
                <div className="card-header bg-[#435690] text-white py-3">
                  <div className="d-flex align-items-center">
                    <FaIcons.FaFileExcel size={24} className="me-3" />
                    <div>
                      <h3 className="opacity-95">
                        Upload your Excel file to import leads
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="card-body p-4">
                  {validationError && (
                    <div className="alert alert-danger" role="alert">
                      <FaIcons.FaExclamationCircle size={20} className="me-2" />
                      {validationError}
                    </div>
                  )}

                  {backendErrors && (
                    <div className="mt-3">
                      <div className="alert alert-danger" role="alert">
                        <FaIcons.FaExclamationCircle
                          size={20}
                          className="me-2"
                        />
                        <strong>{backendErrors?.message}</strong>
                      </div>
                      {Array.isArray(backendErrors?.rejected_rows) &&
                        backendErrors.rejected_rows.length > 0 && (
                          <div className="table-responsive">
                            <h5 className="text-danger mb-3">Rejected Rows</h5>
                            <table className="table table-sm table-bordered">
                              <thead className="table-danger">
                                <tr>
                                  <th>Client Name</th>
                                  <th>Reject Reason</th>
                                </tr>
                              </thead>
                              <tbody>
                                {backendErrors.rejected_rows.map(
                                  (row, index) => (
                                    <tr key={index} className="table-light">
                                      <td>{row.client_name || "N/A"}</td>
                                      <td className="text-danger">
                                        {row.reject_reason || "Unknown error"}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                    </div>
                  )}

                  {!uploadedFile ? (
                    <div
                      className={`upload-area border-2 border-dashed rounded-3 p-5 text-center position-relative ${
                        dragActive
                          ? "border-primary bg-primary bg-opacity-10"
                          : "border-secondary"
                      }`}
                      style={{
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                        minHeight: "300px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <div className="mb-4">
                        <FaIcons.FaCloudUploadAlt
                          size={48}
                          className={
                            dragActive ? "text-primary" : "text-secondary"
                          }
                        />
                      </div>
                      <h5
                        className={
                          dragActive ? "text-primary" : "text-secondary"
                        }
                      >
                        {dragActive
                          ? "Drop your Excel file here"
                          : "Drag & Drop your Excel file here"}
                      </h5>
                      <p className="text-muted mb-4">
                        or <span className="text-primary fw-bold">browse</span>{" "}
                        to choose a file
                      </p>
                      <div className="d-flex flex-wrap justify-content-center gap-2 mb-3">
                        <span className="badge bg-success bg-opacity-10 text-success px-3 py-2">
                          <FaIcons.FaFileExcel size={16} className="me-1" />
                          .xlsx
                        </span>
                        <span className="badge bg-success bg-opacity-10 text-success px-3 py-2">
                          <FaIcons.FaFileExcel size={16} className="me-1" />
                          .xls
                        </span>
                      </div>
                      <small className="text-muted">
                        Maximum file size: 10MB
                      </small>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileChange}
                        className="d-none"
                      />
                    </div>
                  ) : (
                    <div className="uploaded-file-area">
                      <div
                        className={`alert ${
                          validationError || backendErrors
                            ? "alert-danger"
                            : "alert-success"
                        } border-0 shadow-sm`}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <div className="me-3">
                              <FaIcons.FaFileExcel
                                size={24}
                                className={
                                  validationError || backendErrors
                                    ? "text-danger"
                                    : "text-success"
                                }
                              />
                            </div>
                            <div>
                              <h6
                                className={`mb-1 ${
                                  validationError || backendErrors
                                    ? "text-danger"
                                    : "text-success"
                                } fw-bold`}
                              >
                                {validationError || backendErrors
                                  ? "File Validation Failed"
                                  : "File Ready for Upload"}
                              </h6>
                              <p className="mb-0 text-muted small">
                                <strong>{uploadedFile.name}</strong> (
                                {formatFileSize(uploadedFile.size)})
                                {fileData && (
                                  <span className="text-success ms-2">
                                    • {fileData.length} valid leads found
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={removeFile}
                            disabled={uploading}
                          >
                            <FaIcons.FaTrash size={16} />
                          </button>
                        </div>
                      </div>

                      {fileData && !validationError && !backendErrors && (
                        <div className="row mt-4">
                          <div className="col-md-6 mb-3">
                            <div className="card bg-light border-0">
                              <div className="card-body text-center py-3">
                                <FaIcons.FaInfoCircle
                                  size={24}
                                  className="text-info fs-4 mb-2"
                                />
                                <h6 className="mb-1">File Information</h6>
                                <small className="text-muted">
                                  Name: {uploadedFile.name}
                                  <br />
                                  Size: {formatFileSize(uploadedFile.size)}
                                  <br />
                                  Valid Leads: {fileData.length}
                                </small>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6 mb-3">
                            <div className="card bg-light border-0">
                              <div className="card-body text-center py-3">
                                <FaIcons.FaCheckCircle
                                  size={24}
                                  className="text-success fs-4 mb-2"
                                />
                                <h6 className="mb-1">File Validation</h6>
                                <small className="text-success">
                                  ✓ Valid Excel format
                                  <br />✓ Headers validated
                                  <br />✓ Ready to process
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {uploadedFile && fileData && !validationError && (
                    <div className="d-flex gap-3 mt-4">
                      <button
                        type="button"
                        className="btn btn-primary flex-fill py-2"
                        onClick={handleUpload}
                        disabled={uploading}
                      >
                        {uploading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            ></span>
                            Processing...
                          </>
                        ) : (
                          <>
                            <FaIcons.FaUpload size={20} className="me-2" />
                            Upload & Import {fileData.length} Leads
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={removeFile}
                        disabled={uploading}
                      >
                        <FaIcons.FaRedo size={16} className="me-1" />
                        Choose Different File
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="card mt-4 border-0 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title text-primary d-flex gap-1 align-items-center">
                    <FaIcons.FaTable size={20} className="me-2" />
                    <span className="me-3">Required Excel Format</span>
                    <button
                      className="btn btn-outline-success btn-sm d-flex align-items-center"
                      onClick={() => downloadSampleFile("xls")}
                    >
                      <FaIcons.FaFileExcel className="me-1" /> Download Sample
                      (.xls)
                    </button>
                    <button
                      className="btn btn-outline-primary btn-sm d-flex align-items-center"
                      onClick={() => downloadSampleFile("xlsx")}
                    >
                      <FaIcons.FaFileExcel className="me-1" /> Download Sample
                      (.xlsx)
                    </button>
                  </h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="table-primary">
                        <tr className="text-start">
                          <th>Client Name</th>
                          <th>Email</th>
                          <th>
                            Phone <span className="text-danger">*</span>
                          </th>
                          <th>City</th>
                          <th>Destination Country</th>
                          <th>Destination State</th>
                          <th>Previous Education</th>
                          <th>Intended Course</th>
                          <th>Year of Passing</th>
                          <th>CGPA</th>
                          <th>Percentage</th>
                          <th>English Test</th>
                          <th>Time Slot</th>
                          <th>Mode of Class</th>
                          <th>Document Links</th>
                          <th>Budget Min</th>
                          <th>Budget Max</th>
                          <th>Study Gap (Years)</th>
                          <th>Refusal History</th>
                          <th>Visa Type</th>
                        </tr>
                      </thead>

                      <tbody>
                        <tr className="table-light">
                          <td>Abdaal</td>
                          <td>abdaal@gmail.com</td>
                          <td className="text-danger fw-bold">923425915849</td>
                          <td>Islamabad</td>
                          <td>Australia</td>
                          <td>Queensland</td>
                          <td>Bachelor </td>
                          <td>MS Cyber security</td>
                          <td>2023</td>
                          <td>3.4</td>
                          <td>60</td>
                          <td>IELTS</td>
                          <td>9AM-10AM</td>
                          <td>Online</td>
                          <td>Drive link</td>
                          <td>4000000</td>
                          <td>5000000</td>
                          <td>3</td>
                          <td>2</td>
                          <td>Student</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="row mt-3">
                    <div className="col-md-6">
                      <h6 className="text-success">
                        <FaIcons.FaCheckCircle size={20} className="me-2" />
                        Upload Guidelines
                      </h6>
                      <ul className="list-unstyled small text-muted">
                        <li className="mb-1">
                          <FaIcons.FaCheck
                            size={20}
                            className="text-success me-2"
                          />
                          Supported formats: .xlsx, .xls
                        </li>
                        <li className="mb-1">
                          <FaIcons.FaCheck
                            size={20}
                            className="text-success me-2"
                          />
                          Maximum file size: 10MB
                        </li>
                        <li className="mb-1">
                          <FaIcons.FaCheck
                            size={20}
                            className="text-success me-2"
                          />
                          First row must contain exact headers
                        </li>
                        <li className="mb-1">
                          <FaIcons.FaExclamationTriangle className="text-warning me-2" />
                          <span className="text-danger fw-bold">
                            Extra Fields or Columns with their data will be
                            ignored.
                          </span>
                        </li>
                        <li className="mb-1">
                          <FaIcons.FaInfoCircle
                            size={20}
                            className="text-info me-2"
                          />
                          <strong className="text-danger">
                            Budget fields are in MILLIONS
                          </strong>
                        </li>
                      </ul>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-info">
                        <FaIcons.FaInfoCircle size={20} className="me-2" />
                        Field Information
                      </h6>
                      <ul className="list-unstyled small text-muted">
                        <li className="mb-1">
                          <FaIcons.FaPhoneAlt
                            size={20}
                            className="text-danger me-2"
                          />
                          <strong className="text-danger fw-bold">
                            Phone:
                          </strong>{" "}
                          Required field (rows without phone will be skipped)
                        </li>
                        <li className="mb-1 text-danger fw-bold">
                          <FaIcons.FaLanguage
                            size={20}
                            className="text-primary me-2"
                          />
                          <strong>English proficiency test:</strong> Enter
                          (Duolingo,IELTS,PTE,TOEFL,None)
                        </li>

                        <li className="mb-1 text-danger fw-bold">
                          <FaIcons.FaDesktop
                            size={20}
                            className="text-primary me-2"
                          />
                          <strong>Previous Education:</strong>
                          Must be a in ( High School, Intermediate, Diploma,
                          Bachelor, Master, PhD,Other )
                        </li>

                        <li className="mb-1 text-danger fw-bold">
                          <FaIcons.FaStamp
                            size={20}
                            className="text-secondary me-2"
                          />
                          <strong>Visa Type:</strong> will be in
                          (Student,Sponsor,PR,Work)
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {showModal && (
          <>
            {/* Backdrop */}
            <div
              className="modal-backdrop fade show"
              style={{
                display: "block",
                backgroundColor: "rgba(0,0,0,0.45)",
                zIndex: 1040,
              }}
              onClick={() => {
                setShowModal(false);
                setErrors({});
              }}
            />

            {/* Modal */}
            <div
              className="modal fade show d-block"
              tabIndex={-1}
              role="dialog"
              style={{ zIndex: 1050 }}
            >
              <div className="modal-dialog modal-dialog-centered modal-lg">
                <div
                  className="modal-content border-0 rounded-4 shadow"
                  style={{ overflow: "hidden" }}
                >
                  {/* Header */}
                  <div
                    className="modal-header"
                    style={{
                      background: "#f7f8fa",
                      borderBottom: "1px solid #eef2f6",
                    }}
                  >
                    <div>
                      <h5
                        className="modal-title text-dark"
                        style={{ fontWeight: 700 }}
                      >
                        Add Lead
                      </h5>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Fill in the contact information to add a new lead.
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => {
                        setShowModal(false);
                        setErrors({});
                      }}
                    />
                  </div>

                  {/* Body */}
                  <div className="modal-body">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px",
                        padding: "15px",
                      }}
                    >
                      {/* Client Name & Email */}
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="ms-1">
                            Client Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.client_name ? "is-invalid" : ""
                            }`}
                            value={singleLead.client_name}
                            onChange={(e) =>
                              setSingleLead({
                                ...singleLead,
                                client_name: e.target.value,
                              })
                            }
                            placeholder="Enter client name"
                          />
                          {errors.client_name && (
                            <div
                              className="text-danger"
                              style={{
                                fontSize: "0.875em",
                                marginTop: "0.25rem",
                              }}
                            >
                              {errors.client_name}
                            </div>
                          )}
                        </div>

                        <div className="col-md-6">
                          <label className="ms-1">Email Address</label>
                          <input
                            type="email"
                            className={`form-control ${
                              errors.email ? "is-invalid" : ""
                            }`}
                            value={singleLead.email}
                            onChange={(e) =>
                              setSingleLead({
                                ...singleLead,
                                email: e.target.value,
                              })
                            }
                            placeholder="Enter email address"
                          />
                          {errors.email && (
                            <div
                              className="text-danger"
                              style={{
                                fontSize: "0.875em",
                                marginTop: "0.25rem",
                              }}
                            >
                              {errors.email}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Phone Number */}
                      <div className="row g-3">
                        <div className="col-md-6">
                          <CustomPhoneInput
                            value={singleLead.phone}
                            onChange={(value) =>
                              setSingleLead({
                                ...singleLead,
                                phone: value,
                              })
                            }
                            placeholder="Enter phone number"
                            defaultCountry="PK"
                            required
                            className={
                              errors.phone
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                          />
                          {errors.phone && (
                            <div
                              className="text-danger"
                              style={{
                                fontSize: "0.875em",
                                marginTop: "0.25rem",
                              }}
                            >
                              {errors.phone}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div
                    className="modal-footer"
                    style={{
                      background: "#f7f8fa",
                      borderTop: "1px solid #eef2f6",
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setShowModal(false);
                        setErrors({});
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleSaveSingleLead}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default AddLeads;
